import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { LocationDescriptor } from "history";
import { useCallback, useEffect, useMemo, useState } from "react";
import Modal from "react-modal";
import { useParams } from "react-router-dom";
import { Tooltip } from "react-tooltip";
import { DARK_BLUE } from "../../../config";
import { translate } from "../../../intl";
import { useFeatureFlag } from "../../../hooks";
import {
  LinkedItemBox,
  ModalItemWithStripe,
  StyledHrGlobal,
} from "../../../styles/global";
import { LinkedItemInfo } from "../../../types";
import { Colors } from "../../../types/colorStatusNames";
import { vesselsAllowedForNewBarriersLayout } from "../../../utils";
import { PrimaryButton } from "../../Button";
import { LinkedProps } from "../Linked";
import { LinkedItem } from "../LinkedItem";
import {
  ButtonBar,
  CloseModal,
  customStyles,
  Input,
  LinkedContainer,
  ModalBody,
  ModalBox,
  ModalContainer,
  ModalHeader,
  RiskBoxLinkedModal,
  SearchContainer,
  StatusFilterContainer,
  StatusText,
  Title,
} from "./styled";

const statusColors = [Colors.RED, Colors.GREEN, Colors.ORANGE];

export interface ILinkedModalProps extends LinkedProps {
  modalIsOpen: boolean;
  setModalIsOpen: React.Dispatch<React.SetStateAction<boolean>>;
}

function LinkedModal({
  modalIsOpen,
  setModalIsOpen,
  linkedList,
  linkedField,
  linkedFrom,
  linkTo,
  hasTooltip,
  isBarrierModal,
}: ILinkedModalProps) {
  const [activeColorFilter, setActiveColorFilter] = useState<Colors | null>(
    null,
  );
  const [searchValue, setSearchValue] = useState<string>("");
  const { vesselId = "" } = useParams();
  const isBarrierLayoutAllowed =
    useFeatureFlag("barrier-layout-allowed") &&
    vesselsAllowedForNewBarriersLayout.includes(vesselId);

  const handleChange = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setSearchValue(target.value);
  };

  const handleCloseModal = (event: React.MouseEvent<any>) => {
    event.stopPropagation();
    setModalIsOpen(false);
  };

  useEffect(() => {
    if (!modalIsOpen) {
      setSearchValue("");
      setActiveColorFilter(null);
    }
  }, [modalIsOpen]);

  const handleSelectActiveColorFIlter = useCallback(
    (event: React.MouseEvent<HTMLDivElement>) => {
      const color = event.currentTarget.getAttribute("color") as Colors;
      if (activeColorFilter === color) {
        setActiveColorFilter(null);
        return;
      }
      setActiveColorFilter(color);
    },
    [activeColorFilter],
  );

  const filteredData = useMemo(() => {
    if (!linkedList) return null;
    let filteredList = linkedList;
    if (activeColorFilter) {
      filteredList = filteredList.filter((item: LinkedItemInfo | string) => {
        const statusColor = (item as LinkedItemInfo).status_color;
        return statusColor === activeColorFilter;
      });
    }

    if (searchValue) {
      filteredList = filteredList.filter((item: LinkedItemInfo | string) => {
        if (isBarrierModal) {
          return ((item as LinkedItemInfo)?.name ?? item)
            .toLowerCase()
            .includes(searchValue.toLowerCase());
        }
        return String((item as LinkedItemInfo)?.code ?? item)
          .toLowerCase()
          .includes(searchValue.toLowerCase());
      });
    }

    return filteredList;
  }, [activeColorFilter, isBarrierModal, linkedList, searchValue]);

  const statusCount = useMemo(() => {
    return linkedList?.reduce<Record<string, number>>((acc, item) => {
      if (typeof item === "object" && item !== null && "status_color" in item) {
        const color = item.status_color as string;
        acc[color] = (acc[color] || 0) + 1;
      }
      return acc;
    }, {});
  }, [linkedList]);

  const renderModalBody = useMemo(() => {
    return filteredData?.map((item: LinkedItemInfo | string) => {
      const mahOrBarrierCode = String((item as LinkedItemInfo)?.code ?? item);
      const statusCode = (item as LinkedItemInfo)?.status_code;
      const description = (item as LinkedItemInfo)?.name ?? item;
      const link =
        typeof linkTo === "function" && typeof mahOrBarrierCode === "string"
          ? linkTo(mahOrBarrierCode)
          : undefined;

      return (
        <LinkedContainer key={mahOrBarrierCode}>
          {hasTooltip && (
            <Tooltip
              anchorSelect={".tagStatusTooltip"}
              style={{ backgroundColor: DARK_BLUE, zIndex: 2 }}
            />
          )}

          <LinkedItem>
            {isBarrierLayoutAllowed ? (
              <ModalItemWithStripe
                {...(link ? { to: link } : {})}
                as={!link ? "div" : undefined}
                key={mahOrBarrierCode}
                data-tooltip-content={isBarrierModal ? "" : description}
                className="tagStatusTooltip"
                stripecontent={
                  isBarrierModal &&
                  typeof item === "object" &&
                  "hazardCode" in item
                    ? String(item.hazardCode)
                    : ""
                }
                statuscode={statusCode}
                stripeposition="left"
              >
                {isBarrierModal ? description : mahOrBarrierCode}
              </ModalItemWithStripe>
            ) : (
              <LinkedItemBox
                to={link as LocationDescriptor}
                key={mahOrBarrierCode}
                data-tooltip-content={description}
                className="tagStatusTooltip"
              >
                {mahOrBarrierCode}
              </LinkedItemBox>
            )}
          </LinkedItem>
        </LinkedContainer>
      );
    });
  }, [
    filteredData,
    hasTooltip,
    isBarrierLayoutAllowed,
    isBarrierModal,
    linkTo,
  ]);

  const modalTitle = useMemo(() => {
    if (linkedFrom) {
      return translate("generic.linkedOn", { linkedField, linkedFrom });
    }
    return isBarrierLayoutAllowed
      ? linkedField
      : translate("generic.linked", { field: linkedField });
  }, [linkedField, linkedFrom, isBarrierLayoutAllowed]);

  return (
    // @ts-ignore
    <Modal
      isOpen={modalIsOpen}
      onRequestClose={handleCloseModal}
      style={customStyles}
    >
      <ModalContainer>
        <ModalHeader>
          <Title>{modalTitle}</Title>
          <div>
            <CloseModal
              data-testid="closeIcon"
              onClick={(event) => handleCloseModal(event)}
              icon={faTimes}
              color="white"
            />
          </div>
        </ModalHeader>
        <StyledHrGlobal />
        <ModalBox>
          <div style={{ maxHeight: "70vh" }}>
            <SearchContainer isBarrierLayoutAllowed={isBarrierLayoutAllowed}>
              <Input
                data-testid="search-bar"
                onChange={handleChange}
                placeholder={translate("generic.search")}
                value={searchValue}
              />
              {isBarrierLayoutAllowed && (
                <StatusFilterContainer>
                  {statusColors?.map((color) => (
                    <div key={color}>
                      <RiskBoxLinkedModal
                        color={color}
                        active={
                          !activeColorFilter || activeColorFilter === color
                        }
                        onClick={handleSelectActiveColorFIlter}
                      />
                      <StatusText
                        active={
                          !activeColorFilter || activeColorFilter === color
                        }
                      >
                        {statusCount?.[color] ?? 0}
                      </StatusText>
                    </div>
                  ))}
                </StatusFilterContainer>
              )}
            </SearchContainer>
            <ModalBody>{renderModalBody}</ModalBody>
          </div>
          <ButtonBar>
            <PrimaryButton onClick={handleCloseModal}>
              {translate("navigation.close")}
            </PrimaryButton>
          </ButtonBar>
        </ModalBox>
      </ModalContainer>
    </Modal>
  );
}

export default LinkedModal;
